import { OperationType } from 'constant'
import {
  calculateQuantityNotReceived,
  calculateQuantityPicked,
  calculateQuantityReceived,
  calculateQuantityRemaining
} from 'utils'

/**
 * Operation configuration for different operation types
 * This makes it easier to add new operations without hardcoding
 */
export interface OperationConfig {
  type: OperationType
  calculateDisabled: (products: any[]) => boolean
  dialogContent: {
    firstLine: (products: any[]) => string
    secondLine: (products: any[]) => string
  }
  submitHandler: (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => void
}

/**
 * Create operation configurations based on context handlers
 * This function is pure and can be memoized
 */
export const createOperationConfigs = (handlers: {
  handleSubmitPickingSalesOrder: (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => void
  handleSubmitPurchaseOrderReceiving: (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => void
  handleSubmitPickingTransferOrder: (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => void
  handleSubmitScanningTOFBALabel: (
    setOpenDialog: (open: boolean) => void,
    setLoading: (loading: boolean) => void
  ) => void
}): OperationConfig[] => {
  return [
    {
      type: OperationType.SalesOrderPicking,
      calculateDisabled: products => !calculateQuantityPicked(products),
      dialogContent: {
        firstLine: products =>
          `Total Quantity Picked: ${calculateQuantityPicked(products)}`,
        secondLine: products =>
          `Total Quantity Remaining: ${calculateQuantityRemaining(products)}`
      },
      submitHandler: handlers.handleSubmitPickingSalesOrder
    },
    {
      type: OperationType.TransferOrderPicking,
      calculateDisabled: products => !calculateQuantityPicked(products),
      dialogContent: {
        firstLine: products =>
          `Total Quantity Picked: ${calculateQuantityPicked(products)}`,
        secondLine: products =>
          `Total Quantity Remaining: ${calculateQuantityRemaining(products)}`
      },
      submitHandler: handlers.handleSubmitPickingTransferOrder
    },
    {
      type: OperationType.PurchaseOrderReceiving,
      calculateDisabled: products => !calculateQuantityReceived(products),
      dialogContent: {
        firstLine: products =>
          `Total Quantity Received: ${calculateQuantityReceived(products)}`,
        secondLine: products =>
          `Total Quantity Remaining: ${calculateQuantityNotReceived(products)}`
      },
      submitHandler: handlers.handleSubmitPurchaseOrderReceiving
    },
    {
      type: OperationType.TransferOrderFBAPrint,
      calculateDisabled: products => !calculateQuantityPicked(products),
      dialogContent: {
        firstLine: products =>
          `Total Quantity Printed: ${calculateQuantityPicked(products)}`,
        secondLine: products =>
          `Total Quantity Remaining: ${calculateQuantityRemaining(products)}`
      },
      submitHandler: handlers.handleSubmitScanningTOFBALabel
    }
  ]
}

/**
 * Get operation config by type
 * This is more explicit than searching by pathname
 */
export const getOperationConfigByType = (
  configs: OperationConfig[],
  type: OperationType
): OperationConfig | undefined => {
  return configs.find(config => config.type === type)
}
